import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  where,
  limit,
  getCountFromServer,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart, 
  Truck, 
  UserX, 
  MoreHorizontal, 
  ArrowUpRight, 
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  Area, 
  CartesianGrid 
} from 'recharts';

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    dailyRevenue: 0,
    revenueChange: 0,
    ordersToday: 0,
    ordersChange: 0,
    activeDrivers: 0,
    pendingDrivers: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [revenueData, setRevenueData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Single transaction fetch shared between stats + chart, plus the
      // other queries — all run in parallel.
      const [transactions] = await Promise.all([
        fetchAllTransactions(),
        fetchRecentOrders(),
      ]);
      // Stats + revenue chart both reuse `transactions` (no second call).
      await Promise.all([
        fetchStats(transactions),
        computeRevenueChart(transactions),
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTransactions = async () => {
    try {
      const getAllTransactions = httpsCallable(functions, 'getAllTransactions');
      // We only need the last 7 days for both the headline and chart.
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);

      const result = await getAllTransactions({
        page: 1,
        limit: 10000,
        startDate: startDate.toISOString(),
        endDate: null,
        type: null,
      });
      if (!result.data.success) return [];
      return result.data.transactions.map((txn) => ({
        ...txn,
        timestamp: new Date(txn.createdAt),
        amount: parseFloat(txn.amount) || 0,
      }));
    } catch (e) {
      console.error('Error fetching transactions:', e);
      return [];
    }
  };

  const fetchStats = async (allTransactions) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      // Count today/yesterday orders cheaply via aggregation queries.
      const ordersRef = collection(db, 'orders');
      const [todayCountSnap, yesterdayCountSnap, activeDriversSnap, pendingDriversSnap, docsReqDriversSnap] =
        await Promise.all([
          getCountFromServer(
            query(ordersRef, where('createdAt', '>=', Timestamp.fromDate(today))),
          ),
          getCountFromServer(
            query(
              ordersRef,
              where('createdAt', '>=', Timestamp.fromDate(yesterday)),
              where('createdAt', '<', Timestamp.fromDate(today)),
            ),
          ),
          getCountFromServer(
            query(
              collection(db, 'users'),
              where('type', '==', 'driver'),
              where('status', '==', 'active'),
            ),
          ),
          getCountFromServer(
            query(
              collection(db, 'users'),
              where('type', '==', 'driver'),
              where('status', '==', 'pending'),
            ),
          ),
          getCountFromServer(
            query(
              collection(db, 'users'),
              where('type', '==', 'driver'),
              where('status', '==', 'documents_required'),
            ),
          ),
        ]);

      const todayOrdersCount = todayCountSnap.data().count;
      const yesterdayOrdersCount = yesterdayCountSnap.data().count;
      const activeDrivers = activeDriversSnap.data().count;
      const pendingDrivers =
        pendingDriversSnap.data().count + docsReqDriversSnap.data().count;

      // Revenue from the already-fetched transactions.
      const sumAmount = (arr) => arr.reduce((sum, txn) => sum + txn.amount, 0);
      const inWindow = (txn, from, to) =>
        txn.timestamp >= from && (to == null || txn.timestamp < to);

      const todayDeductions = allTransactions.filter(
        (t) => inWindow(t, today, null) && t.type === 'deduction',
      );
      const yesterdayDeductions = allTransactions.filter(
        (t) => inWindow(t, yesterday, today) && t.type === 'deduction',
      );
      const todayGifts = allTransactions.filter(
        (t) => inWindow(t, today, null) && t.type === 'gift',
      );
      const yesterdayGifts = allTransactions.filter(
        (t) => inWindow(t, yesterday, today) && t.type === 'gift',
      );

      const dailyRevenue = Math.max(
        0,
        sumAmount(todayDeductions) - sumAmount(todayGifts),
      );
      const yesterdayRevenue = Math.max(
        0,
        sumAmount(yesterdayDeductions) - sumAmount(yesterdayGifts),
      );

      const revenueChange =
        yesterdayRevenue > 0
          ? ((dailyRevenue - yesterdayRevenue) / yesterdayRevenue * 100).toFixed(1)
          : dailyRevenue > 0
          ? 100
          : 0;

      const ordersChange =
        yesterdayOrdersCount > 0
          ? (
              ((todayOrdersCount - yesterdayOrdersCount) / yesterdayOrdersCount) *
              100
            ).toFixed(1)
          : 0;

      setStats({
        dailyRevenue,
        revenueChange: parseFloat(revenueChange),
        ordersToday: todayOrdersCount,
        ordersChange: parseFloat(ordersChange),
        activeDrivers,
        pendingDrivers,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'), limit(5));
      const snapshot = await getDocs(q);

      // Unique uids to fetch in one batch instead of 10 sequential queries.
      const uids = new Set();
      snapshot.docs.forEach((d) => {
        const o = d.data();
        if (o.customerId) uids.add(o.customerId);
        if (o.driverId) uids.add(o.driverId);
      });
      const userDocs = await Promise.all(
        Array.from(uids).map((uid) => getDoc(doc(db, 'users', uid))),
      );
      const userMap = new Map();
      userDocs.forEach((s) => {
        if (s.exists()) userMap.set(s.id, s.data());
      });

      const orders = snapshot.docs.map((orderDoc) => {
        const orderData = orderDoc.data();

        let customerName = 'Unknown Customer';
        if (orderData.customerId && userMap.has(orderData.customerId)) {
          customerName = userMap.get(orderData.customerId).name || customerName;
        } else if (orderData.senderName) {
          customerName = orderData.senderName;
        }

        let driverName = 'Unassigned';
        if (orderData.driverId && userMap.has(orderData.driverId)) {
          const driverData = userMap.get(orderData.driverId);
          driverName =
            driverData.status === 'deleted'
              ? 'Deleted Account'
              : driverData.name || 'Unknown Driver';
        }

        // Build the row from the existing logic below. The legacy inline
        // function ended here; the rest of the row construction continues
        // unchanged.
        return { orderData, customerName, driverName };
      });

      // Continue with row formatting (matches original return shape).
      const formatted = orders.map(({ orderData, customerName, driverName }) => {
        const orderNum = String(orderData.orderNumber).padStart(8, '0');
        const subNum = orderData.subNumber
          ? String(orderData.subNumber).padStart(2, '0')
          : null;
        const orderNumber = subNum ? `#${orderNum}-${subNum}` : `#${orderNum}`;
        return {
          id: orderNumber,
          type: orderData.serviceType || 'Standard',
          from: orderData.startPlace?.area || 'N/A',
          to: orderData.endPlace?.area || 'N/A',
          customer: customerName,
          driver: driverName,
          status: getStatusLabel(orderData.status),
          amount: orderData.totalPrice || 0,
        };
      });

      setRecentOrders(formatted);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    }
  };

  const computeRevenueChart = async (allTransactions) => {
    try {
      const last7Days = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        last7Days.push(date);
      }

      const revenueByDay = last7Days.map((date) => {
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        const inDay = (txn) =>
          txn.timestamp >= date && txn.timestamp < nextDay;

        const dayDeductions = allTransactions
          .filter((t) => inDay(t) && t.type === 'deduction')
          .reduce((s, t) => s + t.amount, 0);
        const dayGifts = allTransactions
          .filter((t) => inDay(t) && t.type === 'gift')
          .reduce((s, t) => s + t.amount, 0);

        const revenue = Math.max(0, dayDeductions - dayGifts);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        return { day: dayName, revenue };
      });

      setRevenueData(revenueByDay);
    } catch (error) {
      console.error('Error computing revenue chart:', error);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'waiting': 'Pending',
      'pending': 'Pending',
      'accepted': 'In Progress',
      'in_progress': 'In Progress',
      'picked_up': 'In Progress',
      'in_transit': 'In Progress',
      'completed': 'Completed',
      'delivered': 'Completed',
      'canceled': 'Canceled',
      'rejected': 'Canceled',
      'returned': 'Returned',
      'returned_pending': 'Returned'
    };
    return statusMap[status] || status;
  };

  // Dynamic Y-axis formatter based on data values
  const formatYAxis = (value) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };

  // Get max value from revenue data to determine scale
  const getMaxRevenue = () => {
    if (revenueData.length === 0) return 0;
    return Math.max(...revenueData.map(d => d.revenue));
  };

  const StatCard = ({ title, value, change, icon: Icon, trend = 'up', loading = false }) => (
    <div className="bg-white rounded-2xl shadow-soft-lg border border-slate-200/60 p-6 hover:shadow-soft-xl hover:scale-[1.02] transition-all duration-300 group">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-600 mb-2">{title}</p>
          {loading ? (
            <div className="h-9 w-32 bg-slate-200 rounded animate-pulse mb-3"></div>
          ) : (
            <h3 className="text-3xl font-bold text-slate-900 mb-3">{value}</h3>
          )}
          {change !== undefined && !loading && (
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                change >= 0 
                  ? 'bg-gradient-to-r from-success-100 to-success-50 text-success-700 border-success-200' 
                  : 'bg-gradient-to-r from-danger-100 to-danger-50 text-danger-700 border-danger-200'
              }`}>
                {change >= 0 ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                <span>{change >= 0 ? '+' : ''}{change}%</span>
              </div>
              <span className="text-xs text-slate-500">
                {title === "Daily Revenue" || title === "Orders Today" ? "vs yesterday" : ""}
              </span>
            </div>
          )}
        </div>
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-soft ${
          trend === 'up' ? 'bg-gradient-to-br from-success-500 to-success-600' :
          trend === 'down' ? 'bg-gradient-to-br from-danger-500 to-danger-600' :
          'bg-gradient-to-br from-primary-600 to-primary-500'
        }`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'In Progress': { color: 'bg-gradient-to-r from-accent-100 to-accent-50 text-accent-700 border-accent-200', dot: 'bg-accent-500' },
      'Completed': { color: 'bg-gradient-to-r from-success-100 to-success-50 text-success-700 border-success-200', dot: 'bg-success-500' },
      'Pending': { color: 'bg-gradient-to-r from-warning-100 to-warning-50 text-warning-700 border-warning-200', dot: 'bg-warning-500' },
      'Canceled': { color: 'bg-gradient-to-r from-danger-100 to-danger-50 text-danger-700 border-danger-200', dot: 'bg-danger-500' },
      'Returned': { color: 'bg-gradient-to-r from-orange-100 to-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
    };
    
    const config = statusConfig[status] || statusConfig.Pending;
    
    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${config.color}`}>
        <div className={`w-2 h-2 rounded-full ${config.dot} animate-pulse`} />
        {status}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard Overview</h1>
          <p className="text-slate-600 mt-2">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={fetchDashboardData}
            disabled={loading}
            className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border-2 border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          title="Daily Revenue"
          value={`${stats.dailyRevenue.toLocaleString()} JOD`}
          change={stats.revenueChange}
          icon={DollarSign}
          trend={stats.revenueChange >= 0 ? "up" : "down"}
          loading={loading}
        />
        <StatCard
          title="Orders Today"
          value={stats.ordersToday}
          change={stats.ordersChange}
          icon={ShoppingCart}
          trend={stats.ordersChange >= 0 ? "up" : "down"}
          loading={loading}
        />
        <StatCard
          title="Active Drivers"
          value={stats.activeDrivers}
          icon={Truck}
          loading={loading}
        />
        <StatCard
          title="Pending Drivers"
          value={stats.pendingDrivers}
          icon={UserX}
          trend="down"
          loading={loading}
        />
      </div>

      {/* Charts and Tables Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Revenue Trend Chart - Takes 2/3 on large screens */}
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-soft-lg border border-slate-200/60 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Revenue Trend</h2>
              <p className="text-sm text-slate-500 mt-1">Last 7 days performance</p>
            </div>
            <button className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>
          <div className="h-80">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#9333ea" stopOpacity={0.15}/>
                      <stop offset="100%" stopColor="#9333ea" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    tickFormatter={formatYAxis}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '16px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      padding: '12px'
                    }}
                    formatter={(value) => [`${value.toLocaleString()} JOD`, 'Revenue']}
                    labelStyle={{ fontWeight: 600, marginBottom: '4px', color: '#0f172a' }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="none" fill="url(#revenueGradient)" />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#9333ea"
                    strokeWidth={3}
                    dot={{ fill: '#9333ea', r: 5, strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 7, fill: '#9333ea', stroke: '#fff', strokeWidth: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Recent Orders - Takes 1/3 on large screens */}
        <div className="bg-white rounded-2xl shadow-soft-lg border border-slate-200/60 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Recent Orders</h2>
                <p className="text-sm text-slate-500 mt-1">Latest order activity</p>
              </div>
              <button 
                onClick={() => navigate('/orders')}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                View All
              </button>
            </div>
          </div>
          <div className="overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
                </div>
              ) : recentOrders.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No recent orders</p>
                  <p className="text-sm mt-1">Orders will appear here once created</p>
                </div>
              ) : (
                recentOrders.map((order) => (
                  <div key={order.id} className="p-5 border-b border-slate-50 last:border-b-0 hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">{order.id}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{order.type}</p>
                      </div>
                      <StatusBadge status={order.status} />
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Route</span>
                        <span className="font-medium text-slate-900">{order.from} → {order.to}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Customer</span>
                        <span className="font-medium text-slate-900">{order.customer}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Driver</span>
                        <span className={`font-medium ${order.driver === 'Unassigned' ? 'text-warning-600' : 'text-slate-900'}`}>
                          {order.driver}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <span className="text-lg font-bold text-slate-900">{order.amount.toFixed(2)} JOD</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Width Recent Orders Table for smaller screens */}
      <div className="bg-white rounded-2xl shadow-soft-lg border border-slate-200/60 overflow-hidden lg:hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Recent Orders</h2>
              <p className="text-sm text-slate-500 mt-1">Latest order activity</p>
            </div>
            <button 
              onClick={() => navigate('/orders')}
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-xl transition-colors"
            >
              View All
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No recent orders</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100/50 border-b-2 border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Route</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{order.id}</div>
                        <div className="text-sm text-slate-500">{order.type}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm text-slate-900">{order.from}</div>
                        <div className="text-sm text-slate-500">→ {order.to}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">{order.amount.toFixed(2)} JOD</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}